import { useMsal } from '@azure/msal-react';
import { Badge, Button, makeStyles, Menu, MenuButtonProps, MenuItem, MenuList, MenuPopover, MenuTrigger, Option, SplitButton, Text, Tooltip } from '@fluentui/react-components';
import { EditRegular, StarFilled } from '@fluentui/react-icons';
import { format, parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { observer } from 'mobx-react-lite';
import React, { JSX, useEffect, useState } from 'react';
import { Navigate, NavLink, useNavigate, useParams } from 'react-router';
import { StatusResponse } from '../../api';
import { dateTimeStringFormat } from '../../common/date/datetime-string-format';
import { getISO8601PacificDateFromDateTime } from '../../common/date/get-iso-8601-pacific-date';
import { downloadFile } from '../../common/file/download-file';
import { useCommonStyles, useStores } from '../../common/hooks';
import { scrubbingStateBadge } from '../../common/scrubbing/scrubbing-state-badge';
import { sortAwardNominationRefs } from '../../common/sorting/sort-award-nomination-ref';
import { sortCountryNominationRefs } from '../../common/sorting/sort-country-nomination-ref';
import { TooltipFieldName } from '../../common/tooltip/tooltip-field-name';
import { getTooltipContentByFieldName } from '../../common/tooltip/tooltip-get-content';
import { DocumentType } from '../../common/types/document-types';
import { AssignedNominationStatus, blankJudge, blankJudgementStatus, blankNominationScrubbingStatus, Country, DisqualifyReasonType, DocumentMetadata, Judge, Judgement, JudgementStatus, JudgementStatusType, NominationRef, NominationScrubbingStatus, Reviewer, RoleName, ScrubbingStatusId, User } from '../../models';
import { ResourceTicketRequest, ResourceTypes } from '../../models/resource-services';
import { NominationForStaffRouteParams } from '../../models/route-params';
import { RoutePaths } from '../../route-paths';
import { useAppToast } from '../../shared/toast';
import { ConfirmationDialog, CustomMessageBar, PageTitle, RoundedHorizontalBarChart } from '../shared-components';
import Loading from '../shared-components/Loading';
import { ExportNominationsModal, ExportType } from '../shared-components/export-nominations-modal/ExportNominationsModal';
import { FlexBox } from '../shared-components/flex-box';
import { SelectOptions } from '../shared-components/form';
import { DisqualifyScrubbingModal, PassScrubbingModal } from '../shared-components/scrubbing-modal';
import './NominationForStaffPage.scss';
import { AssignCountriesAssociationModal } from './components/AssignCountriesAssociationModal';
import { AssignJudgeModal } from './components/AssignJudgeModal';
import { AssignNominationReviewerModal } from './components/AssignNominationReviewerModal';
import { CustomerReferences } from './components/CustomerReferences';
import { NominationDetails } from './components/NominationDetails';
import { NominationForStaffSummary } from './components/NominationForStaffSummary';
import { RemoveJudgingModal } from './components/RemoveJudgingModal';
import { SaveAsDraftJudgingModal } from './components/SaveAsDraftJudgingModal';
import { SubmitJudgingModal } from './components/SubmitJudgingModal';

const useStyles = makeStyles({
  selectOptions: { display: 'flex', borderBottom: '1px solid #B3B0AD' },
  selectOptionsLabel: { margin: 0 },
  selectOptionsInputContainer: { border: 0 },
});

const NominationForStaffPageImpl: React.FC = () => {
  const {
    instance,
  } = useMsal();

  const commonStyles = useCommonStyles();
  const styles = useStyles();

  const [isNotFound, setIsNotFound] = useState<boolean>(false);
  const [isShowRemoveJudgingModal, setShowRemoveJudgingModal] = useState(false);
  const [isShowSaveAsDraftJudgingModal, setShowSaveAsDraftJudgingModal] = useState(false);
  const [isShowSubmitJudgingModal, setShowSubmitJudgingModal] = useState(false);
  const [isShowAssignJudgeModal, setShowAssignJudgeModal] = useState(false);
  const [isShowAssignNominationReviewerModal, setShowAssignNominationReviewerModal] = useState<boolean>(false);
  const [isUnassignDialogOpen, setIsUnassignDialogOpen] = useState<boolean>(false);
  const [isJudgingValidated, setJudgingValidated] = useState(false);
  const [isNextNominationToPreviewAvailable, setNextNominationToPreviewAvailable] = useState(true);
  const [selectedNominationId, setSelectedNominationId] = useState<number>(0);
  const [isShowExportNominationModal, setShowExportNominationModal] = useState<boolean>(false);

  const {
    awardId,
    nominationId,
    countryId,
  } = useParams<NominationForStaffRouteParams>();

  const navigate = useNavigate();

  const {
    apiStore,
    apiEndpointCallStatesStore,
    awardStore,
    userStore,
    nominationForStaffPageUiStore,
    partnerTypeStore,
  } = useStores();

  const isDownloadServiceInFlight = apiEndpointCallStatesStore.inFlightStatusMap.get('postResourceTicket') ?? false;
  const isAdminRole = userStore.isAdmin || userStore.isGroupLead || userStore.isAwardLead || userStore.isCountryLead;
  const isAuthenticatedUserAssignedJudge = nominationForStaffPageUiStore.isAssignedJudge(userStore.userId);
  const canManageJudging = isAdminRole && !isAuthenticatedUserAssignedJudge;

  const { showToast } = useAppToast();
  const currentCountryId = countryId ? Number(countryId) : undefined;
  const hasCountryAwardContext = !nominationForStaffPageUiStore.isCountryAward || currentCountryId !== undefined;

  const getNominationForStaff = async (nominationId: number, countryId?: number): Promise<void> => {
    try {
      const nomination = await apiStore.getNominationForStaff(instance, nominationId, countryId);
      nominationForStaffPageUiStore.setNominationForStaff(nomination);
    } catch (error) {
      setIsNotFound(true);
    }
  }

  const getAwardById = async (awardId: number): Promise<void> => {
    try {
      const award = await apiStore.getAwardById(instance, awardId);
      nominationForStaffPageUiStore.setSelectedAward(award);
    } catch (error) {
      // TODO - handle error
    }
  }

  const getNominationScrubbingStatus = async (awardId: number, nominationId: number, countryId?: number): Promise<void> => {
    try {
      if (nominationForStaffPageUiStore.isCountryAward) {
        if (!awardStore.defaultProgramRef || countryId === undefined) return;

        const nominationScrubbingStatus = await apiStore.getCountryAwardNominationScrubbingStatus(
          instance,
          awardStore.defaultProgramRef.programId,
          countryId,
          nominationId,
        );

        nominationForStaffPageUiStore.setScrubbingStatus(nominationScrubbingStatus.scrubbingStatus);
        return;
      }

      const nominationScrubbingStatus = await apiStore.getAwardNominationScrubbingStatus(
        instance,
        awardId,
        nominationId,
      );

      nominationForStaffPageUiStore.setScrubbingStatus(nominationScrubbingStatus.scrubbingStatus);
    } catch (error) {
      // TODO: Handle error
    }
  }

  const getNominationJudgement = async (nominationId: number, userId: number): Promise<void> => {
    let request: Promise<Judgement>;
    if (nominationForStaffPageUiStore.isCountryAward) {
      if (currentCountryId === undefined) return;

      request = apiStore.getCountryNominationJudgement(instance, nominationId, userId, currentCountryId);
    } else {
      request = apiStore.getNominationJudgement(instance, nominationId, userId);
    }

    try {
      const nominationJudgement = await request;
      nominationForStaffPageUiStore.setNominationJudgement(nominationJudgement);
    } catch (error) {
      // TODO - handle error
    }
  }

  const getNominationJudgementCount = async (nominationId: number): Promise<void> => {
    let request: Promise<number>;
    if (nominationForStaffPageUiStore.isCountryAward) {
      if (currentCountryId === undefined) return;

      request = apiStore.getCountryNominationJudgementCount(instance, nominationId, currentCountryId);
    } else {
      request = apiStore.getNominationJudgementCount(instance, nominationId);
    }

    try {
      const judgementCount = await request;
      nominationForStaffPageUiStore.setNominationJudgementCount(judgementCount);
    } catch (error) {
      // TODO - handle error
    }
  }

  const getAssignedJudge = async (nominationId: number, userId: number): Promise<void> => {
    try {
      const assignedJudge = await apiStore.getAssignedJudge(instance, nominationId, userId, currentCountryId);
      nominationForStaffPageUiStore.setAssignedJudge(assignedJudge);
    } catch (error) {
      // TODO - handle error
    }
  }

  const getAssignedJudges = async (nominationId: number): Promise<void> => {
    try {
      const assignedJudges = await apiStore.getAssignedJudges(instance, nominationId, currentCountryId);
      nominationForStaffPageUiStore.setAssignedJudges(assignedJudges ?? []);
    } catch (error) {
      // TODO - handle error
    }
  }

  const getJudgementStatus = async (nominationId: number, userId: number): Promise<void> => {
    try {
      const judgementStatus = await apiStore.getJudgementStatus(instance, nominationId, userId, currentCountryId);
      nominationForStaffPageUiStore.setJudgementStatus(judgementStatus);
    } catch (error) {
      // TODO - handle error
    }
  }

  const getExecutiveSummaryDocumentMetadata = async (nominationId: number): Promise<void> => {
    try {
      const executiveSummaryDocument = await apiStore.getExecutiveSummaryDocumentMetadata(instance, nominationId);
      nominationForStaffPageUiStore.setExecutiveSummaryDocument(executiveSummaryDocument);
    } catch (error) {
      // TODO - handle error
    }
  }

  const handleSelectedJudgeChange = async (judgeUserId: number): Promise<void> => {
    nominationForStaffPageUiStore.setSelectedJudgeUserId(judgeUserId);
    nominationForStaffPageUiStore.resetNominationJudging();
    const nominationId = nominationForStaffPageUiStore.nominationForStaff.nomination.nominationId;
    await Promise.allSettled([
      getNominationJudgement(nominationId, judgeUserId),
      getAssignedJudge(nominationId, judgeUserId),
      getJudgementStatus(nominationId, judgeUserId),
    ]);
  }

  const loadNominationForStaff = async (awardId: number, nominationId: number, countryId?: number): Promise<void> => {
    if (nominationForStaffPageUiStore.isCountryAward) {
      if (!countryId) return;

      if (!userStore.isJudge && nominationForStaffPageUiStore.sortedCountryNominationRefs.length === 0) {
        if (!awardStore.defaultProgramRef) return;

        const countryNominationRefs = await apiStore.getCountryNominationRefs(instance, awardStore.defaultProgramRef.programId, countryId);
        const sortedRefs = sortCountryNominationRefs(countryNominationRefs, false);
        nominationForStaffPageUiStore.setSortedCountryNominationRefs(sortedRefs);
        const staffProgramDates = await apiStore.getProgramDatesForStaff(instance, awardStore.defaultProgramRef.programId);
        nominationForStaffPageUiStore.setStaffProgramDates(staffProgramDates);
      }
    } else {
      if (!userStore.isJudge) {
        if (nominationForStaffPageUiStore.sortedAwardNominationRefs.length === 0) {
          const awardNominationRefs = await apiStore.getAwardNominationRefs(instance, awardId);
          let sortedRefs: NominationRef[] = [];
          if (awardNominationRefs) {
            if (awardStore.isReviewAndDesignateWindowOpen || awardStore.isComplianceAndRevisionsWindowOpen || awardStore.isWinnersWindowOpen) {
              sortedRefs = sortAwardNominationRefs(awardNominationRefs, 'SCORE', true);
            } else {
              sortedRefs = sortAwardNominationRefs(awardNominationRefs, 'ROLE', false);
            }

            nominationForStaffPageUiStore.setSortedAwardNominationRefs(sortedRefs);
          }
        }
      }
    }

    nominationForStaffPageUiStore.setExecutiveSummaryDocument(undefined);

    if (nominationForStaffPageUiStore.nominationForStaff.nomination.award.requireWrittenNomination) {
      await getWrittenExecutiveSummary(nominationId);
    } else {
      await getExecutiveSummaryDocumentMetadata(nominationId);
    }

    if (awardStore.isJudgingWindowOpen) {
      nominationForStaffPageUiStore.resetNominationJudging();
      await getCurrentAssignedJudge(nominationId);
    }

    const isCurrentUserAssignedJudge = nominationForStaffPageUiStore.isAssignedJudge(userStore.userId);
    const canReviewScrubbingStatus = (userStore.isAdmin || userStore.isLead || userStore.isReviewer)
      && !isCurrentUserAssignedJudge;
    if ((awardStore.isScrubbingWindowOpen || awardStore.isJudgingWindowOpen) && canReviewScrubbingStatus) {
      await getNominationScrubbingStatus(awardId, nominationId, countryId);
      await getAssignedNominationReviewer(nominationId);
    }

    if (awardStore.isJudgingWindowOpen) {
      if (isAdminRole && !isCurrentUserAssignedJudge) {
        await getAssignedJudges(nominationId);
        if (nominationForStaffPageUiStore.assignedJudges.length > 0) {
          const judgeUserId = nominationForStaffPageUiStore.getSelectedAssignedJudgeUserId(userStore.userId);
          if (judgeUserId === undefined) return;

          nominationForStaffPageUiStore.setSelectedJudgeUserId(judgeUserId);

          await Promise.all([
            getNominationJudgement(nominationId, judgeUserId),
            getAssignedJudge(nominationId, judgeUserId),
            getJudgementStatus(nominationId, judgeUserId),
          ]);
        }
      }
    }

    if (awardStore.isReviewAndDesignateWindowOpen || awardStore.isComplianceAndRevisionsWindowOpen || awardStore.isWinnersWindowOpen) {
      if (nominationForStaffPageUiStore.isCountryAward) {
        await getCountryAwardNominationRole(nominationId);
      } else {
        await getAwardNominationRole(nominationId);
      }
    }

    nominationForStaffPageUiStore.setUploadedMedia([]);
    if (userStore.isAdmin && (nominationForStaffPageUiStore.nominationRole?.role.toLowerCase() === 'winner' || nominationForStaffPageUiStore.nominationRole?.role.toLowerCase() === 'finalist')) {
      const uploadedMedia = await apiStore.getUploadedMedia(instance, awardId, nominationId);
      nominationForStaffPageUiStore.setUploadedMedia(uploadedMedia);
    }
  }

  const openScrubbingPassModal = (isVisible: boolean) => {
    nominationForStaffPageUiStore.setScrubbingPassedModalVisible(isVisible);
  }

  const openScrubbingDisqualifyModal = (isVisible: boolean) => {
    nominationForStaffPageUiStore.setScrubbingDisqualifiedModalVisible(isVisible);
  }

  const getCurrentAssignedJudge = async (nominationId: number): Promise<void> => {
    try {
      if (userStore.isJudge && !(userStore.isAdmin || userStore.isGroupLead || userStore.isAwardLead)) {
        const userId = userStore.userId;
        if (userId === undefined) return;

        await getAssignedJudge(nominationId, userId);

        if (!nominationForStaffPageUiStore.isAssignedJudge(userId)) return;

        await getNominationJudgement(nominationId, userId);
        await getJudgementStatus(nominationId, userId);
      }
    } catch (error) {
      // TODO - handle error
    }
  }

  const getAwardNominationRole = async (nominationId: number): Promise<void> => {
    try {
      const nominationRole = await apiStore.getAwardNominationRole(instance, Number(awardId), nominationId);
      nominationForStaffPageUiStore.setNominationRole(nominationRole);
    } catch (error) {
      // TODO - handle error
    }
  }

  const getCountryAwardNominationRole = async (nominationId: number): Promise<void> => {
    if (!awardStore.defaultProgramRef || !countryId) return;
    try {
      const nominationRole = await apiStore.getCountryAwardNominationRole(instance, awardStore.defaultProgramRef.programId, Number(countryId), nominationId);
      nominationForStaffPageUiStore.setNominationRole(nominationRole);
    } catch (error) {
      // TODO: Handle error
    }
  }

  const updateAwardNominationScrubbingStatus = async (awardId: number, nominationId: number, scrubbingStatus: NominationScrubbingStatus): Promise<void> => {
    try {
      await apiStore.updateAwardNominationScrubbingStatus(instance, awardId, nominationId, scrubbingStatus);
    } catch (error) {
      // TODO - handle error
    }
  }

  const updateCountryAwardNominationScrubbingStatus = async (programId: number, countryId: number, nominationId: number, scrubbingStatus: NominationScrubbingStatus): Promise<void> => {
    try {
      await apiStore.updateCountryAwardNominationScrubbingStatus(instance, programId, countryId, nominationId, scrubbingStatus);
    } catch (error) {
      // TODO: Handle error
    }
  }

  const updateNominationJudgementStatus = async (nominationId: number, userId: number, judgementStatus: JudgementStatus): Promise<void> => {
    try {
      await apiStore.updateJudgementStatus(instance, nominationId, userId, judgementStatus, currentCountryId);
    } catch (error) {
      // TODO - handle error
    }
  }

  const updateNominationScrubbingStatus = async (
    status: 'Passed' | 'Disqualified',
    disqualifyReason?: DisqualifyReasonType,
    feedback?: string
  ): Promise<void> => {
    const defaultProgramRef = awardStore.defaultProgramRef;
    if (nominationForStaffPageUiStore.isCountryAward && (!defaultProgramRef || currentCountryId === undefined)) return;

    nominationForStaffPageUiStore.setIsLoading(true);

    const nominationId = nominationForStaffPageUiStore.nominationForStaff.nominationId;
    const scrubbingStatus = {
      ...blankNominationScrubbingStatus(),
      nominationId,
      scrubbingStatus: status,
      disqualifyReason,
      feedback
    };

    try {
      if (nominationForStaffPageUiStore.isCountryAward) {
        if (!defaultProgramRef || currentCountryId === undefined) return;

        await updateCountryAwardNominationScrubbingStatus(
          defaultProgramRef.programId,
          currentCountryId,
          nominationId,
          scrubbingStatus
        );
      } else {
        await updateAwardNominationScrubbingStatus(
          Number(awardId),
          nominationId,
          scrubbingStatus
        );
      }

      await Promise.all([
        getNominationForStaff(nominationId, currentCountryId),
        getNominationScrubbingStatus(Number(awardId), nominationId, currentCountryId),
      ]);
    } finally {
      const isPass = status === 'Passed';
      isPass ? openScrubbingPassModal(false) : openScrubbingDisqualifyModal(false);
      nominationForStaffPageUiStore.setIsLoading(false);
    }
  }

  const redirectAfterJudging = (): void => {
    if (userStore.isJudge) {
      userStore.setIsShowPartnerPage(!userStore.isStaff);
      navigate(RoutePaths.Home, { replace: true });
      return;
    }

    if (nominationForStaffPageUiStore.isCountryAward) {
      if (currentCountryId === undefined) return;

      navigate(`${RoutePaths.CountryAward}/${currentCountryId}`, { replace: true });
    } else {
      navigate(RoutePaths.Awards + '/' + awardId, { replace: true });
    }
  }

  const redirectToNextAwardNominationIfAvailable = async (userId: number): Promise<void> => {
    const nominationIdRef = await apiStore.getJudgeAssignedNomination(instance, Number(awardId), userId);

    if (nominationIdRef.status === AssignedNominationStatus.Assigned) {
      navigate(
        `${RoutePaths.Awards}/${awardId}${RoutePaths.Nominations}/${nominationIdRef.nominationId}`,
        { replace: true },
      );
      return;
    }
    redirectAfterJudging();
  }

  const redirectToNextCountryNominationIfAvailable = async (userId: number, countryId: number): Promise<void> => {
    const nominationIdRef = await apiStore.getCountryJudgeAssignedNomination(instance, Number(awardId), userId, countryId);

    if (nominationIdRef.status === AssignedNominationStatus.Assigned) {
      navigate(
        `${RoutePaths.Awards}/${awardId}${RoutePaths.Nominations}/${nominationIdRef.nominationId}/${RoutePaths.Countries}/${countryId}`,
        { replace: true },
      );
      return;
    }
    redirectAfterJudging();
  }

  const redirectToNextNominationIfAvailable = async (): Promise<void> => {
    if (userStore.userId === undefined) return;

    nominationForStaffPageUiStore.setIsLoading(true);
    try {
      if (nominationForStaffPageUiStore.isCountryAward) {
        if (currentCountryId === undefined) return;

        await redirectToNextCountryNominationIfAvailable(userStore.userId, currentCountryId);
        return;
      }
      await redirectToNextAwardNominationIfAvailable(userStore.userId);
    } catch {
      redirectAfterJudging();
    } finally {
      nominationForStaffPageUiStore.setIsLoading(false);
    }
  }

  const getNextNomination = (): void => {
    if (!nominationForStaffPageUiStore.nextNominationId) return;

    if (nominationForStaffPageUiStore.isCountryAward) {
      if (currentCountryId === undefined) return;

      navigate(
        `${RoutePaths.Awards}/${awardId}${RoutePaths.Nominations}/${nominationForStaffPageUiStore.nextNominationId}${RoutePaths.Countries}/${currentCountryId}`,
      );
      return;
    }

    navigate(
      `${RoutePaths.Awards}/${awardId}${RoutePaths.Nominations}/${nominationForStaffPageUiStore.nextNominationId}`,
    );
  }

  const updateNominationJudgement = async (nominationId: number, userId: number, judgement: Judgement): Promise<void> => {
    let request: Promise<void>;
    if (nominationForStaffPageUiStore.isCountryAward) {
      if (currentCountryId === undefined) return;

      request = apiStore.updateCountryNominationJudgement(instance, nominationId, userId, judgement, currentCountryId);
    } else {
      request = apiStore.updateNominationJudgement(instance, nominationId, userId, judgement);
    }

    try {
      await request;
    } catch (error) {
      // TODO - handle error
    }
  }

  const handleSaveJudgingAsDraft = async (): Promise<void> => {
    const userId = userStore.userId;
    const nominationJudgement = nominationForStaffPageUiStore.nominationJudgement;
    if (userId === undefined || !nominationJudgement) return;

    try {
      nominationForStaffPageUiStore.setIsLoading(true);

      await Promise.all([
        updateNominationJudgementStatus(nominationForStaffPageUiStore.nominationForStaff.nominationId, userId, buildJudgementStatus('Draft')),
        updateNominationJudgement(nominationForStaffPageUiStore.nominationForStaff.nominationId, userId, nominationJudgement),
      ]);

      redirectAfterJudging();
    } catch (error) {
      // TODO - handle error
    } finally {
      nominationForStaffPageUiStore.setIsLoading(false);
    }
  }

  const buildJudgementStatus = (status: JudgementStatusType): JudgementStatus => {
    const judgementStatus = blankJudgementStatus();
    judgementStatus.judgementId = nominationForStaffPageUiStore.nominationJudgement ? nominationForStaffPageUiStore.nominationJudgement.judgementId : 0;
    judgementStatus.nominationId = nominationForStaffPageUiStore.nominationForStaff.nominationId;
    judgementStatus.status = status;
    return judgementStatus;
  }

  const skipToNextJudge = async (): Promise<void> => {
    const selectedJudgeUserId = nominationForStaffPageUiStore.selectedJudgeUserId;
    if (selectedJudgeUserId === undefined) return;

    nominationForStaffPageUiStore.setIsLoading(true);
    try {
      await updateNominationJudgementStatus(
        nominationForStaffPageUiStore.nominationForStaff.nominationId,
        selectedJudgeUserId,
        buildJudgementStatus('Removed'),
      );
      await loadAssignedJudges();
    } finally {
      nominationForStaffPageUiStore.setIsLoading(false);
      setShowAssignJudgeModal(false);
    }
  }

  const removeJudging = async (callbackFn: () => void): Promise<void> => {
    const userId = userStore.userId;
    if (userId === undefined) return;

    nominationForStaffPageUiStore.setIsLoading(true);
    try {
      await updateNominationJudgementStatus(
        nominationForStaffPageUiStore.nominationForStaff.nominationId,
        userId,
        buildJudgementStatus('Removed'),
      );
      setShowRemoveJudgingModal(false);
      callbackFn();
    } finally {
      nominationForStaffPageUiStore.setIsLoading(false);
    }
  }

  const submitJudging = async (callbackFn: () => void): Promise<void> => {
    const userId = userStore.userId;
    const nominationJudgement = nominationForStaffPageUiStore.nominationJudgement;
    if (userId === undefined || !nominationJudgement) return;

    try {
      nominationForStaffPageUiStore.setIsLoading(true);
      await updateNominationJudgement(nominationForStaffPageUiStore.nominationForStaff.nominationId, userId, nominationJudgement);

      if (nominationForStaffPageUiStore.judgementStatus?.status !== 'Submitted') {
        await updateNominationJudgementStatus(
          nominationForStaffPageUiStore.nominationForStaff.nominationId,
          userId,
          buildJudgementStatus('Submitted'),
        );

        setShowSubmitJudgingModal(false);
        callbackFn();
      } else {
        setShowSubmitJudgingModal(false);
        callbackFn();
      }
    } catch (error) {

      // TODO - handle error
    } finally {
      nominationForStaffPageUiStore.setIsLoading(false);
    }
  }

  const handleRemoveJudgingAndExit = async (): Promise<void> => {
    await removeJudging(() => redirectAfterJudging());
  }

  const handleRemoveJudgingAndContinue = async (): Promise<void> => {
    await removeJudging(() => redirectToNextNominationIfAvailable());
  }

  const handleSubmitJudgingAndExit = async (): Promise<void> => {
    await submitJudging(() => redirectAfterJudging());
  }

  const handleSubmitJudgingAndContinue = async (): Promise<void> => {
    await submitJudging(() => redirectToNextNominationIfAvailable());
  }

  const getPreviewNextNomination = async (): Promise<void> => {
    if (userStore.userId === undefined || !hasCountryAwardContext) return;

    nominationForStaffPageUiStore.setIsLoading(true);
    setNextNominationToPreviewAvailable(true);

    try {
      const nextNominationIdRef = await apiStore.getPreviewNextNomination(
        instance,
        Number(awardId),
        userStore.userId,
        nominationForStaffPageUiStore.nominationForStaff.nominationId,
        currentCountryId
      );

      switch (nextNominationIdRef.status) {
        case AssignedNominationStatus.Assigned:
          if (nominationForStaffPageUiStore.nominationForStaff.nominationId === nextNominationIdRef.nominationId) {
            setNextNominationToPreviewAvailable(false);
          } else {
            const countryPath = currentCountryId === undefined
              ? ''
              : `${RoutePaths.Countries}/${currentCountryId}`;

            navigate(
              `${RoutePaths.Awards}/${Number(awardId)}${RoutePaths.Nominations}/${nextNominationIdRef.nominationId}${countryPath}`,
              { replace: true },
            );
          }
          break;

        default:
          return;
      }

    } catch (error) {
      // TODO - handle error
    } finally {
      nominationForStaffPageUiStore.setIsLoading(false);
    }
  }

  const getWrittenExecutiveSummary = async (nominationId: number): Promise<void> => {
    try {
      const writtenSummary = await apiStore.getWrittenExecutiveSummary(instance, nominationId);
      nominationForStaffPageUiStore.setWrittenExecutiveSummary(writtenSummary);
    } catch (error) {
      // TODO - handle error
    }
  }

  const updateWrittenExecutiveSummary = async (nominationId: number): Promise<void> => {
    if (nominationForStaffPageUiStore.writtenExecutiveSummaryContent === undefined) return;
    try {
      await apiStore.updateWrittenExecutiveSummary(instance, nominationId, nominationForStaffPageUiStore.writtenExecutiveSummaryContent);
    } catch (error) {
      showToast({
        title: (error as StatusResponse).message,
        intent: 'error',
      });
    }
  }

  const loadAssignedJudges = async (): Promise<void> => {
    const nominationId = nominationForStaffPageUiStore.nominationForStaff.nominationId;
    const assignedJudges = await apiStore.getAssignedJudges(instance, nominationId, currentCountryId);
    nominationForStaffPageUiStore.setAssignedJudges(assignedJudges ?? []);
    if (assignedJudges.length > 0) {
      const judgeUserId = nominationForStaffPageUiStore.getSelectedAssignedJudgeUserId(userStore.userId);
      if (judgeUserId === undefined) return;

      nominationForStaffPageUiStore.setSelectedJudgeUserId(judgeUserId);

      await Promise.allSettled([
        getNominationJudgement(nominationId, judgeUserId),
        getAssignedJudge(nominationId, judgeUserId),
        getJudgementStatus(nominationId, judgeUserId),
      ]);
    }
  }

  const handleSaveAssignedJudge = async (user: User): Promise<void> => {
    const selectedJudgeUserId = nominationForStaffPageUiStore.selectedJudgeUserId;
    if (nominationForStaffPageUiStore.assignedJudges.length > 0 && selectedJudgeUserId === undefined) return;
    if (!hasCountryAwardContext) return;

    nominationForStaffPageUiStore.setIsLoading(true);
    try {
      const judge = blankJudge();
      judge.user.emailAddress = user.emailAddress;
      judge.assignmentDate = getISO8601PacificDateFromDateTime(new Date());
      if (nominationForStaffPageUiStore.assignedJudges.length > 0) {
        if (selectedJudgeUserId === undefined) return;

        await apiStore.updateAssignedJudge(
          instance,
          nominationForStaffPageUiStore.nominationForStaff.nominationId,
          selectedJudgeUserId,
          judge,
          currentCountryId,
        );
      } else {
        await apiStore.insertAssignedJudge(
          instance,
          nominationForStaffPageUiStore.nominationForStaff.nominationId,
          judge,
          currentCountryId,
        );
      }
      await loadAssignedJudges();
      setShowAssignJudgeModal(false);
    } catch (error) {
      // TODO - handle error
    } finally {
      nominationForStaffPageUiStore.setIsLoading(false);
      setShowAssignJudgeModal(false);
    }
  }

  const associatedCountryString = (countries: Country[], countryId: number): string => {
    return countries.find(country => country.countryId === countryId)?.displayName ?? '';
  }

  const handleUpdateIsCaseStudy = async (isCaseStudy: boolean): Promise<void> => {
    if (!hasCountryAwardContext) return;

    nominationForStaffPageUiStore.setIsLoading(true);
    const nominationId = nominationForStaffPageUiStore.nominationForStaff.nominationId;

    try {
      await apiStore.updateNominationIsCaseStudy(
        instance,
        nominationId,
        isCaseStudy,
        currentCountryId,
      );
      await getNominationForStaff(nominationId, currentCountryId);
    } catch (error) {
      // TODO - handle error
    } finally {
      nominationForStaffPageUiStore.setIsLoading(false);
    }
  };

  const isJudgingValid = (): boolean => {
    const nominationJudgement = nominationForStaffPageUiStore.nominationJudgement;
    if (!nominationJudgement) return false;

    return !nominationJudgement.questions.some((question) => !question.rating || question.rating === 0)
      && (!nominationJudgement.feedback || nominationJudgement.feedback.length <= 1000);
  };

  const renderJudgingSection = (): JSX.Element => {
    if (!awardStore.isJudgingWindowOpen || !(userStore.isJudge || userStore.isNominationJudge)) {
      return <></>;
    }

    if (nominationForStaffPageUiStore.isAssignedJudge(userStore.userId)) {
      return renderJudgingButton();
    }

    if (nominationForStaffPageUiStore.isCountryAward) return <></>;

    return (
      <>
        <Button
          appearance='secondary'
          onClick={() => redirectToNextNominationIfAvailable()}
        >
          Back
        </Button>
        <Tooltip
          withArrow
          content={{
            children: getTooltipContentByFieldName('nomination-for-staff-page', 'nextJudgingButton') || '',
            className: commonStyles.tooltip,
          }}
          relationship='description'
        >
          <Button
            appearance='primary'
            onClick={() => getPreviewNextNomination()}
          >
            Next
          </Button>
        </Tooltip>
      </>
    );
  };

  const handleSubmit = (): void => {
    setJudgingValidated(true);
    if (isJudgingValid()) {
      setShowSubmitJudgingModal(true);
    }
  };

  const dropdownItems: {
    key: string;
    label: string;
    onClick: () => void;
    tooltipField: TooltipFieldName;
  }[] = [
      {
        key: 'remove',
        label: 'Remove',
        onClick: () => setShowRemoveJudgingModal(true),
        tooltipField: 'removeJudgingButton'
      },
      {
        key: 'next',
        label: 'Next',
        onClick: getPreviewNextNomination,
        tooltipField: 'nextJudgingButton'
      },
      {
        key: 'draft',
        label: 'Save as draft',
        onClick: () => setShowSaveAsDraftJudgingModal(true),
        tooltipField: 'saveAsDraftJudgingButton'
      }
    ];

  const renderJudgingButton = (): React.JSX.Element => {
    return (
      <>
        {nominationForStaffPageUiStore.isJudgementSubmitted ? (
          <>
            <Button
              appearance='secondary'
              onClick={redirectAfterJudging}
            >
              Cancel
            </Button>
            <Button
              appearance='primary'
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </>
        ) : (
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              {(triggerProps: MenuButtonProps) => (
                <SplitButton
                  appearance='primary'
                  menuButton={triggerProps}
                  primaryActionButton={{ onClick: handleSubmit }}
                >
                  Submit
                </SplitButton>
              )}
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {
                  dropdownItems.map((item) => (
                    <Tooltip
                      withArrow
                      key={item.key}
                      relationship='description'
                      content={{
                        children: getTooltipContentByFieldName('nomination-for-staff-page', item.tooltipField) || '',
                        className: commonStyles.tooltip,
                      }}
                    >
                      <MenuItem
                        onClick={item.onClick}
                      >
                        {item.label}
                      </MenuItem>
                    </Tooltip>
                  ))
                }
              </MenuList>
            </MenuPopover>
          </Menu>
        )}
      </>
    );
  };

  const renderScrubbingStatusActions = (): React.JSX.Element => {
    return (
      <FlexBox
        direction='column'
        className='scrubbing-status-actions'
      >
        <FlexBox
          aligns='center'
        >
          <Text
            as='b'
            weight='semibold'
          >
            Scrubbing status:
          </Text>
          {
            nominationForStaffPageUiStore.scrubbingStatus
              ? scrubbingStateBadge(
                ScrubbingStatusId[nominationForStaffPageUiStore.scrubbingStatus],
                nominationForStaffPageUiStore.scrubbingStatus,
              )
              : <Badge appearance='tint' color='informative'>
                Not reviewed
              </Badge>
          }
        </FlexBox>
        <FlexBox>
          <Button
            appearance='secondary'
            disabled={nominationForStaffPageUiStore.scrubbingStatus === 'Passed'}
            onClick={() => openScrubbingPassModal(true)}
          >
            Pass
          </Button>
          <Button
            appearance='secondary'
            disabled={nominationForStaffPageUiStore.scrubbingStatus === 'Disqualified'}
            onClick={() => openScrubbingDisqualifyModal(true)}
          >
            Disqualify
          </Button>
        </FlexBox>
      </FlexBox>
    );
  };

  const resetPage = async (): Promise<void> => {
    nominationForStaffPageUiStore.setExecutiveSummaryDocument(undefined);
    nominationForStaffPageUiStore.setIsBeingValidated(false);
    nominationForStaffPageUiStore.setIsEditingMode(false);
    nominationForStaffPageUiStore.resetStates();
    await preloadData();
  };

  const restoreDocument = async (documentType: DocumentType): Promise<void> => {
    nominationForStaffPageUiStore.setIsLoading(true);
    const nominationId = nominationForStaffPageUiStore.nominationForStaff.nominationId;
    try {
      if (documentType === 'executive-summary') {
        await apiStore.restoreExecutiveSummaryFileContent(instance, nominationId);
      } else {
        await apiStore.restoreSupportingDocument(instance, nominationId);
      }
      await resetPage();
    } catch (error) {
      // TODO - handle error
    } finally {
      nominationForStaffPageUiStore.setIsLoading(false);
    }
  }

  const saveNominationOrganization = async (): Promise<void> => {
    const organization = nominationForStaffPageUiStore.nominationForStaffOrganization;
    if (!organization) return;

    await apiStore.updateNominationOrganization(instance, nominationForStaffPageUiStore.nominationForStaff.nominationId, organization);
  };

  const saveEditedNomination = async (): Promise<void> => {
    const isNominationValid = nominationForStaffPageUiStore.isNominationValid(partnerTypeStore.otherPartnerTypeId);
    nominationForStaffPageUiStore.setIsBeingValidated(!isNominationValid);
    if (!isNominationValid) return;

    nominationForStaffPageUiStore.setIsLoading(true);
    const saveTasks = [
      apiStore.updateNomination(instance, nominationForStaffPageUiStore.nominationForStaff.nomination),
      saveNominationOrganization(),
      apiStore.updateNominationAbstract(instance, nominationForStaffPageUiStore.nominationForStaff.nominationId, nominationForStaffPageUiStore.nominationForStaff.nomination.abstract),
      apiStore.updateNominationPartnerTypes(instance, nominationForStaffPageUiStore.nominationForStaff.nominationId, nominationForStaffPageUiStore.nominationForStaff.nomination.partnerTypes),
      apiStore.updateNominationMarketplaceOffer(instance, nominationForStaffPageUiStore.nominationForStaff.nominationId, nominationForStaffPageUiStore.nominationForStaff.nomination.marketplaceOfferUrl),
    ];

    if (nominationForStaffPageUiStore.isDeleteExecutiveSummaryFile)
      saveTasks.push(apiStore.deleteExecutiveSummaryFileContent(instance, nominationForStaffPageUiStore.nominationForStaff.nominationId));

    nominationForStaffPageUiStore.supportingDocumentIdsToDelete.forEach(documentIdToDelete => {
      if (documentIdToDelete >= 0) saveTasks.push(apiStore.deleteSupportingDocument(instance, documentIdToDelete));
    });

    nominationForStaffPageUiStore.nominationForStaff.nomination.contacts.map(contact => saveTasks.push(apiStore.updateNominationContact(instance, contact)));
    nominationForStaffPageUiStore.nominationForStaff.nomination.jointNominees.map(nominee => saveTasks.push(apiStore.updateOrganization(instance, nominee)));
    nominationForStaffPageUiStore.nominationForStaff.nomination.customerReferences.map(customerReference => saveTasks.push(apiStore.updateCustomerReference(instance, customerReference)));

    if (nominationForStaffPageUiStore.nominationForStaff.nomination.award.requireWrittenNomination)
      saveTasks.push(updateWrittenExecutiveSummary(nominationForStaffPageUiStore.nominationForStaff.nominationId));

    const uploadTask: Promise<DocumentMetadata>[] = [];
    nominationForStaffPageUiStore.documentToUpload.forEach(document => {
      if (document.documentType === 'executive-summary') {
        uploadTask.push(apiStore.uploadExecutiveSummaryFileContent(instance, document.nominationId, document.content));
      }
      else if (document.documentType === 'supporting-document') {
        uploadTask.push(apiStore.uploadSupportingDocument(instance, document.nominationId, document.content));
      }
    });

    await Promise.all(saveTasks)
      .catch(() => resetPage());
    await Promise.all(uploadTask)
      .finally(() => resetPage());
  }

  const download = async (req: ResourceTicketRequest): Promise<void> => {
    try {
      const resourceTicket = await apiStore.postResourceTicket(instance, req);
      downloadFile(resourceTicket.ticketId);
    } catch (error) {
      // TODO - handle error
    }
  }

  const handleDownloadUploadedMedia = async (): Promise<void> => {
    await Promise.all(
      nominationForStaffPageUiStore.uploadedMedia.map(async (uploadedMedia) => download({
        data: {
          routeData: {
            uploadedMediaId: uploadedMedia.uploadedMediaId
          }
        },
        resourceType: ResourceTypes.UploadedMedia
      }))
    );
  }

  const handleExportNomination = async (exportedType: ExportType): Promise<void> => {
    if (selectedNominationId > 0) {
      await download({
        data: {
          routeData: {
            nominationId: selectedNominationId,
          },
          queryString: {
            exportType: exportedType,
          },
        },
        resourceType: ResourceTypes.ExportedNomination,
      });
    }
    setShowExportNominationModal(false);
    setSelectedNominationId(0);
  }

  const getAssignedNominationReviewer = async (nominationId: number): Promise<void> => {
    try {
      let assignedReviewer: Reviewer | undefined;
      if (nominationForStaffPageUiStore.isCountryAward) {
        if (!awardStore.defaultProgramRef || currentCountryId === undefined) return;

        assignedReviewer = await apiStore.getAssignedCountryNominationReviewer(
          instance,
          awardStore.defaultProgramRef.programId,
          currentCountryId,
          nominationId,
        );
      } else {
        assignedReviewer = await apiStore.getAssignedNominationReviewer(instance, nominationId);
      }

      nominationForStaffPageUiStore.setCurrentReviewer(assignedReviewer);
    } catch (error) {
      // TODO: Handle error
      nominationForStaffPageUiStore.setCurrentReviewer(undefined);
    }
  }

  const deleteNominationReviewer = async (): Promise<void> => {
    if (!nominationForStaffPageUiStore.currentReviewer) return;
    if (nominationForStaffPageUiStore.isCountryAward && (!awardStore.defaultProgramRef || currentCountryId === undefined)) return;

    nominationForStaffPageUiStore.setIsLoading(true);
    try {
      const nominationId = nominationForStaffPageUiStore.nominationForStaff.nominationId;

      if (nominationForStaffPageUiStore.isCountryAward) {
        if (!awardStore.defaultProgramRef || currentCountryId === undefined) return;

        await apiStore.deleteCountryNominationReviewer(
          instance,
          awardStore.defaultProgramRef.programId,
          currentCountryId,
          nominationId,
          nominationForStaffPageUiStore.currentReviewer.id,
        );
      } else {
        await apiStore.deleteNominationReviewer(
          instance,
          nominationId,
          nominationForStaffPageUiStore.currentReviewer.id,
        );
      }
      setIsUnassignDialogOpen(false);
      await getAssignedNominationReviewer(nominationId);
      await getNominationScrubbingStatus(Number(awardId), nominationId, currentCountryId);
    } catch (error) {
      // TODO: Handle error
    } finally {
      nominationForStaffPageUiStore.setIsLoading(false);
    }
  }

  const renderJudgeName = (user?: User): string => {
    if (!user) return '';
    return user.userId ? `${user.firstName} ${user.lastName}` : user.emailAddress;
  }

  const preloadData = async (): Promise<void> => {
    nominationForStaffPageUiStore.setIsLoading(true);

    if (!nominationId || !awardId) {
      nominationForStaffPageUiStore.setIsLoading(false);
      return;
    }

    const currentNominationId = Number(nominationId);
    const currentAwardId = Number(awardId);

    await Promise.allSettled([
      getAwardById(currentAwardId),
      getNominationForStaff(currentNominationId, currentCountryId),
    ]);

    if (nominationForStaffPageUiStore.isCountryAward && currentCountryId === undefined) {
      setIsNotFound(true);
      nominationForStaffPageUiStore.setIsLoading(false);
      return;
    }

    await Promise.allSettled([
      getNominationJudgementCount(currentNominationId),
      loadNominationForStaff(currentAwardId, currentNominationId, currentCountryId),
    ]);

    nominationForStaffPageUiStore.setIsLoading(false);
  }

  const handleEditAssociatedCountries = (): void => {
    nominationForStaffPageUiStore.setEditAssociatedCountryModalType('update');
  }

  const handleDismissAssignCountriesAssociationModal = async (needRefresh?: boolean): Promise<void> => {
    nominationForStaffPageUiStore.setEditAssociatedCountryModalType(undefined);
    if (needRefresh) {
      await preloadData();
    }
  }

  const getSelectedAssignedJudge = (): Judge | undefined => {
    return nominationForStaffPageUiStore.assignedJudges.find(
      (assignedJudge) => assignedJudge.user.userId === nominationForStaffPageUiStore.selectedJudgeUserId,
    ) ?? nominationForStaffPageUiStore.assignedJudges.at(0);
  }

  const generateAssignSinceJudgeInputValue = (): string => {
    if (canManageJudging) {
      const assignedJudge = getSelectedAssignedJudge();
      if (!assignedJudge?.assignmentDate) return '';
      return dateTimeStringFormat(assignedJudge.assignmentDate.toString(), 'MM/dd/yyyy');
    }
    if (!userStore.isJudge || !nominationForStaffPageUiStore.assignedJudge?.assignmentDate) return '';
    return dateTimeStringFormat(nominationForStaffPageUiStore.assignedJudge.assignmentDate.toString(), 'MM/dd/yyyy');
  };

  useEffect(() => {
    preloadData();
    return () => {
      nominationForStaffPageUiStore.resetStates();
    };
  }, [nominationId, awardId, countryId]);

  if (isNotFound) {
    return (
      <Navigate
        to={RoutePaths.NotFound}
        replace
      />
    )
  }

  return (
    <div className='nomination-for-staff-page'>
      {(nominationForStaffPageUiStore.isLoading || isDownloadServiceInFlight) && <Loading />}
      <AssignCountriesAssociationModal
        isModalOpen={nominationForStaffPageUiStore.editAssociatedCountryModalType !== undefined}
        selectedNomination={nominationForStaffPageUiStore.nominationForStaff.nomination}
        onDismissModal={(needRefresh) => handleDismissAssignCountriesAssociationModal(needRefresh)}
      />
      {
        nominationForStaffPageUiStore.isScrubbingPassedModalVisible &&
        <PassScrubbingModal
          onCancelButtonClicked={() => openScrubbingPassModal(false)}
          onSaveButtonClicked={() => updateNominationScrubbingStatus('Passed')}
        />
      }
      {
        nominationForStaffPageUiStore.isScrubbingDisqualifiedModalVisible &&
        <DisqualifyScrubbingModal
          onCancelButtonClicked={() => openScrubbingDisqualifyModal(false)}
          onSaveButtonClicked={(disqualify, feedback) => updateNominationScrubbingStatus('Disqualified', disqualify, feedback)}
        />
      }
      <RemoveJudgingModal
        isModalOpen={isShowRemoveJudgingModal}
        onDismissModal={() => setShowRemoveJudgingModal(false)}
        onRemoveAndContinueButtonClicked={() => handleRemoveJudgingAndContinue()}
        onRemoveAndExitButtonClicked={() => handleRemoveJudgingAndExit()}
        isHideContinueButton={!userStore.isJudge}
      />
      {
        isShowSaveAsDraftJudgingModal &&
        <SaveAsDraftJudgingModal
          onNoButtonClicked={() => setShowSaveAsDraftJudgingModal(false)}
          onYesButtonClicked={() => handleSaveJudgingAsDraft()}
        />
      }
      <SubmitJudgingModal
        isModalOpen={isShowSubmitJudgingModal}
        onDismissModal={() => setShowSubmitJudgingModal(false)}
        onSubmitAndNextButtonClicked={() => handleSubmitJudgingAndContinue()}
        onSubmitAndExitButtonClicked={() => handleSubmitJudgingAndExit()}
        isHideContinueButton={!userStore.isJudge}
      />
      {
        isShowExportNominationModal &&
        <ExportNominationsModal
          isModalOpen={isShowExportNominationModal}
          isDownloading={isDownloadServiceInFlight}
          onCancelButtonClicked={() => {
            setShowExportNominationModal(false);
            setSelectedNominationId(0);
          }}
          onExportButtonClicked={(exportedType) => handleExportNomination(exportedType)}
        />
      }
      {
        hasCountryAwardContext && (userStore.isAdmin || userStore.isLead) && isShowAssignJudgeModal &&
        <AssignJudgeModal
          onCancel={() => setShowAssignJudgeModal(false)}
          onSave={user => handleSaveAssignedJudge(user)}
          onSkipToNextJudge={() => skipToNextJudge()}
          isNominationHasJudge={nominationForStaffPageUiStore.hasAssignedJudge}
        />
      }
      <AssignNominationReviewerModal
        isModalOpen={hasCountryAwardContext && isShowAssignNominationReviewerModal}
        currentReviewer={nominationForStaffPageUiStore.currentReviewer}
        onDismissModal={() => setShowAssignNominationReviewerModal(false)}
      />
      <ConfirmationDialog
        isOpen={isUnassignDialogOpen}
        dialogTitle='Unassign a Reviewer'
        dialogSubtext={nominationForStaffPageUiStore.currentReviewer && (<p className='fs-6'>
          Are you sure you want to unassign the reviewer <strong className='fw-semibold'>{nominationForStaffPageUiStore.currentReviewer.fullName || nominationForStaffPageUiStore.currentReviewer.emailAddress}</strong>?
        </p>)}
        onPrimaryButtonClick={() => deleteNominationReviewer()}
        onSecondaryButtonClick={() => setIsUnassignDialogOpen(false)}
        primaryButtonLabel='Yes'
        secondaryButtonLabel='No, go back'
        onCloseDialogClick={() => setIsUnassignDialogOpen(false)}
      />
      {
        isNextNominationToPreviewAvailable === false &&
        <CustomMessageBar
          message={'There is no available nomination to preview.'}
          notificationTypeCode='warning'
          showDismissButton
        />
      }
      <FlexBox
        isFullWidth
        aligns='end'
        content='space-between'
      >
        <div>
          <PageTitle
            title={
              nominationForStaffPageUiStore.isCountryAward ?
                `${associatedCountryString(nominationForStaffPageUiStore.nominationForStaff.nomination.associatedCountries, Number(countryId))} - Country award` :
                `${nominationForStaffPageUiStore.selectedAward?.title}`
            }
            subtitle={awardStore.defaultProgramRef?.displayName}
          />
          <NavLink
            to={nominationForStaffPageUiStore.isCountryAward ?
              RoutePaths.CountryAward + '/' + Number(countryId) :
              RoutePaths.Awards + '/' + nominationForStaffPageUiStore.selectedAward?.awardId
            }
            end
          >
            {'< Back to award dashboard'}
          </NavLink>
        </div>
        {
          awardStore.isJudgingWindowOpen &&
          <RoundedHorizontalBarChart
            id='nomination-judgement-count'
            primaryData={{
              name: 'Completed judgements',
              color: '#0078D4',
              value: nominationForStaffPageUiStore.nominationJudgementCount,
            }}
            secondaryData={{
              name: 'Required judgements',
              color: '#50E6FF',
              value: nominationForStaffPageUiStore.maxJudgementCount,
            }}
            renderDescription={(
              <Text
                block
                align='end'
              >
                Judges scored: {nominationForStaffPageUiStore.nominationJudgementCount} of {nominationForStaffPageUiStore.maxJudgementCount}
              </Text>
            )}
          />
        }
      </FlexBox>
      {
        ((isAdminRole && !userStore.isJudge) || nominationForStaffPageUiStore.nominationRole?.role === 'Winner' || nominationForStaffPageUiStore.nominationRole?.role === 'Finalist') && (
          <FlexBox
            isFullWidth
            content='end'
            aligns='center'
            className='nomination-for-staff-page__actions-top'
          >
            {
              nominationForStaffPageUiStore.nominationRole?.role === 'Winner' &&
              <FlexBox
                className='tag--winner'
                aligns='center'
                content='center'
              >
                <StarFilled
                  color='#FFB900'
                />
                <Text>Winner</Text>
              </FlexBox>
            }
            {
              nominationForStaffPageUiStore.nominationRole?.role === 'Finalist' &&
              <FlexBox
                className='tag--finalist'
                aligns='center'
                content='center'
              >
                <StarFilled
                  color='#BDD8E3'
                />
                <Text>Finalist</Text>
              </FlexBox>
            }
            {
              ((userStore.isAdmin || userStore.isGroupLead || userStore.isLead) && !userStore.isJudge) &&
              <Button
                appearance='secondary'
                onClick={getNextNomination}
              >
                View next nomination
              </Button>
            }
          </FlexBox>
        )
      }
      <FlexBox
        className='nomination-for-staff-page__actions-bottom'
        content='space-between'
        aligns='end'
      >
        <FlexBox
          aligns='start'
          className='as-read-only-input'
        >
          <Text as='b' weight='semibold'>Country association:</Text>
          <Text>{nominationForStaffPageUiStore.associatedCountryDisplayNames}</Text>
          {
            userStore.isAdmin && nominationForStaffPageUiStore.isCountryAward && (
              <Button
                appearance='transparent'
                title='Edit'
                icon={<EditRegular />}
                onClick={handleEditAssociatedCountries}
              />
            )
          }
        </FlexBox>

        <FlexBox
          wrap
          aligns='end'
        >
          {
            awardStore.isJudgingWindowOpen &&
            nominationForStaffPageUiStore.scrubbingStatus === 'Passed' &&
            nominationForStaffPageUiStore.nominationForStaff.isJudged === false &&
            <>
              <FlexBox
                aligns='start'
                className='as-read-only-input'
              >
                <Text as='b' weight='semibold'>Assigned since:</Text>
                <Text>{generateAssignSinceJudgeInputValue()}</Text>
              </FlexBox>
              {
                canManageJudging && (
                  <SelectOptions
                    label='Assigned to: '
                    orientation='horizontal'
                    className={styles.selectOptions}
                    dropdownProps={{
                      className: styles.selectOptionsInputContainer,
                      value: renderJudgeName(getSelectedAssignedJudge()?.user) ?? '',
                      selectedOptions: nominationForStaffPageUiStore.selectedJudgeUserId === undefined
                        ? []
                        : [`${nominationForStaffPageUiStore.selectedJudgeUserId}`],
                    }}
                    onOptionSelect={(data) => {
                      if (!data.optionValue) return;
                      handleSelectedJudgeChange(Number(data.optionValue));
                    }}
                  >
                    {nominationForStaffPageUiStore.assignedJudges.map(judge =>
                      <Option
                        key={judge.user.emailAddress}
                        value={`${judge.user.userId}`}
                        disabled={!judge.user.userId}
                      >
                        {renderJudgeName(judge.user)}
                      </Option>)
                    }
                  </SelectOptions>
                )
              }
              {
                !canManageJudging && userStore.isJudge && (
                  <FlexBox
                    aligns='start'
                    className='as-read-only-input'
                  >
                    <Text as='b' weight='semibold'>Assigned to:</Text>
                    <Text>{nominationForStaffPageUiStore.assignedJudge ? renderJudgeName(nominationForStaffPageUiStore.assignedJudge.user) : ''}</Text>
                  </FlexBox>
                )
              }
              {
                hasCountryAwardContext && canManageJudging && nominationForStaffPageUiStore.judgementStatus?.status !== 'Submitted' &&
                <Tooltip
                  relationship='description'
                  positioning='before'
                  content={{
                    children: 'Select a different judge or owner. This can be helpful if a judge is taking too long to review the nomination.',
                    className: commonStyles.tooltip,
                  }}
                  withArrow
                >
                  <Button
                    appearance='secondary'
                    onClick={() => setShowAssignJudgeModal(true)}
                  >
                    Assign new judge
                  </Button>
                </Tooltip>
              }
            </>
          }
          {
            !awardStore.isJudgingWindowOpen &&
            (awardStore.isScrubbingWindowOpen || (userStore.isCountryNominationReviewer && nominationForStaffPageUiStore.isScrubbingWindowAlreadyOpenButWinnersWindowNotStartYet)) && (
              <>
                {renderScrubbingStatusActions()}
                <FlexBox className='as-read-only-input'>
                  <Text as='b' weight='semibold'>Assigned since:</Text>
                  <Text>
                    {
                      nominationForStaffPageUiStore.currentReviewer && nominationForStaffPageUiStore.currentReviewer.assignedSince &&
                      <span>{format(utcToZonedTime(parseISO(nominationForStaffPageUiStore.currentReviewer.assignedSince), 'America/Los_Angeles'), 'MM/dd/yyyy')}</span>
                    }
                  </Text>
                </FlexBox>
                <FlexBox className='as-read-only-input'>
                  <Text as='b' weight='semibold'>Assigned to:</Text>
                  <Text>
                    {
                      nominationForStaffPageUiStore.currentReviewer &&
                      <span>{nominationForStaffPageUiStore.currentReviewer.fullName || nominationForStaffPageUiStore.currentReviewer.emailAddress}</span>
                    }
                  </Text>
                </FlexBox>
                {
                  userStore.isAuthorized([RoleName.Admin, RoleName.AwardLead, RoleName.AwardNominationReviewer, RoleName.CountryLead]) &&
                  hasCountryAwardContext &&
                  nominationForStaffPageUiStore.currentReviewer && (
                    <Button
                      appearance='secondary'
                      onClick={() => setIsUnassignDialogOpen(true)}
                    >
                      Unassign
                    </Button>
                  )
                }
                {
                  userStore.isAuthorized([RoleName.Admin, RoleName.AwardLead, RoleName.AwardNominationReviewer, RoleName.CountryLead]) &&
                  hasCountryAwardContext &&
                  !nominationForStaffPageUiStore.scrubbingStatus &&
                  (
                    <Button
                      appearance='secondary'
                      onClick={() => setShowAssignNominationReviewerModal(true)}
                    >
                      Assign new reviewer
                    </Button>
                  )
                }
              </>
            )
          }
          {renderJudgingSection()}
        </FlexBox>
      </FlexBox>
      <div className='nomination-for-staff-page__main'>
        <NominationForStaffSummary
          isJudgingValidated={isJudgingValidated}
          onCancelEditNomination={() => resetPage()}
          onSaveNomination={() => saveEditedNomination()}
          downloadUploadedMedia={() => handleDownloadUploadedMedia()}
          downloadExportedNomination={(nominationId) => {
            setSelectedNominationId(nominationId);
            setShowExportNominationModal(true);
          }}
          onCaseStudyCheckedChange={handleUpdateIsCaseStudy}
          onRestoreDocument={documentType => restoreDocument(documentType)}
        />
        {
          (userStore.isAdmin || userStore.isLead || userStore.isReviewer) &&
          <CustomerReferences />
        }
        {
          (userStore.isAdmin || userStore.isLead || userStore.isReviewer) &&
          <NominationDetails />
        }
      </div>
    </div>
  );
}

export const NominationForStaffPage = observer(NominationForStaffPageImpl);
